import type { TimetableRow } from './EventTimetable';

export type SortOrder = 'soonest' | 'nearest' | 'cheapest';

/**
 * Orders results that are already in the browser.
 *
 * <p>Sorting lives here rather than in the API because the API sends the whole result set — every
 * upcoming event in the country, or everything inside the radius. When the client already holds
 * all the rows, sorting them is free and instant, needs no round trip, and keeps the list and the
 * map showing the same thing without coordinating two requests.
 *
 * <p>That stops being true the day results are paginated. You cannot sort a page: you have to
 * order the whole set and then take a slice, so a client that sorts page one produces confidently
 * wrong answers. When pagination arrives, this function's signature becomes the API's `sort`
 * parameter and the ordering moves into the query — which is why it's a pure function over rows
 * rather than logic tangled into the component.
 */
export function sortRows(rows: TimetableRow[], order: SortOrder): TimetableRow[] {
  // Soonest is how the rows arrived — the query already orders by start time — so there is
  // nothing to do and no copy to make.
  if (order === 'soonest') return rows;

  // Copied because sort mutates, and these rows come from a memoised value that other things read.
  const sorted = [...rows];

  // Array.prototype.sort is stable, so events at equal distance or equal price stay in the order
  // the API gave them: soonest first. Ties resolve to the more useful answer for free.
  if (order === 'nearest') {
    return sorted.sort((a, b) => compareAbsentLast(a.distance ?? null, b.distance ?? null));
  }
  return sorted.sort((a, b) => compareAbsentLast(cheapest(a), cheapest(b)));
}

/**
 * The lowest an attendee could pay, or null when the organiser stated nothing.
 *
 * <p>Note this is not zero. An event with no stated price is not a free event, and treating it as
 * one would float every unpriced listing to the top of a cheapest-first sort — advertising a
 * price on somebody else's behalf that they never claimed.
 */
function cheapest(row: TimetableRow): number | null {
  return row.event.price ? row.event.price.minMinor : null;
}

/** Absent values sort last in both directions, rather than pretending to be zero. */
function compareAbsentLast(a: number | null, b: number | null): number {
  if (a === null) return b === null ? 0 : 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * Which orderings the current results can actually offer.
 *
 * <p>Distance needs somewhere to measure from, and price needs at least one event that states one.
 * Offering either without the data behind it gives someone a control that appears to do nothing.
 */
export function availableSorts(rows: TimetableRow[], hasOrigin: boolean): SortOrder[] {
  const orders: SortOrder[] = ['soonest'];
  if (hasOrigin) orders.push('nearest');
  if (rows.some((row) => row.event.price !== null)) orders.push('cheapest');
  return orders;
}

export const SORT_LABELS: Record<SortOrder, string> = {
  soonest: 'Soonest first',
  nearest: 'Nearest first',
  cheapest: 'Cheapest first',
};
